import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { computed } from 'vue'

import { useUserStore } from '@/stores/user'

// 扩展 dayjs 插件
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(weekOfYear)
dayjs.extend(quarterOfYear)

// 货币符号映射（扩展版本，参考老系统）
const CURRENCY_SYMBOLS: Record<string, string> = {
  // 主要货币
  USD: '$',
  EUR: '€',
  GBP: '£',
  CNY: '¥',
  JPY: '¥',
  KRW: '₩',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'Fr',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RUB: '₽',
  BRL: 'R$',
  MXN: '$',
  INR: '₹',
  SGD: 'S$',
  HKD: 'HK$',
  TWD: 'NT$',
  THB: '฿',
  MYR: 'RM',
  PHP: '₱',
  IDR: 'Rp',
  VND: '₫',
  ZAR: 'R',
  TRY: '₺',
  ILS: '₪',
  AED: 'د.إ',
  SAR: '﷼',
  EGP: 'E£',
  NZD: 'NZ$',
  CLP: '$',
  COP: '$',
  PEN: 'S/',
  UYU: '$U',
  ARS: '$',
  BGN: 'лв',
  RON: 'lei',
  HRK: 'kn',
  ISK: 'kr',
  LTL: 'Lt',
  LVL: 'Ls',
  EEK: 'kr',
  MTL: '₤',
  CYP: '£',
  SIT: 'SIT',
  SKK: 'Sk',
}

export function useTeam() {
  const userStore = useUserStore()

  // 获取当前团队
  const currentTeam = computed(() => userStore.getCurrentTeam())

  // 获取当前团队的货币代码
  const teamCurrency = computed(() => {
    const team = currentTeam.value
    if (!team || !team.currency) {
      return 'USD' // 默认货币
    }
    return team.currency.toUpperCase()
  })

  // 获取货币符号
  const currencySymbol = computed(() => {
    const currency = teamCurrency.value
    return CURRENCY_SYMBOLS[currency] || '$'
  })

  // 获取当前团队的时区
  const teamTimezone = computed(() => {
    const team = currentTeam.value
    return team?.timezone || 'UTC'
  })

  // 格式化金额（带货币符号）
  const formatCurrency = (amount: number | string, options?: {
    showSymbol?: boolean
    position?: 'before' | 'after'
    precision?: number
    thousands?: string
    decimal?: string
  }) => {
    const {
      showSymbol = true,
      position = 'before',
      precision = 2,
      thousands = ',',
      decimal = '.',
    } = options || {}

    // 转换为数字
    const numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount
    if (Number.isNaN(numAmount))
      return '0'

    // 格式化数字
    const formatted = numAmount.toFixed(precision)
      .replace(/\B(?=(\d{3})+(?!\d))/g, thousands)
      .replace('.', decimal)

    if (!showSymbol)
      return formatted

    const symbol = currencySymbol.value
    return position === 'before' ? `${symbol}${formatted}` : `${formatted}${symbol}`
  }

  // 获取所有可用的货币选项
  const availableCurrencies = computed(() => {
    return Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => ({
      code,
      symbol,
      label: `${code} (${symbol})`,
    }))
  })

  // 获取货币符号（静态方法）
  const getCurrencySymbol = (currencyCode: string): string => {
    return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] || '$'
  }

  // 检查是否为有效货币代码
  const isValidCurrency = (currencyCode: string): boolean => {
    return currencyCode.toUpperCase() in CURRENCY_SYMBOLS
  }

  /**
   * 将北京时区的时间转换为团队所在时区的时间
   * @param dateInput 北京时区的时间，可以是字符串、Date对象或dayjs对象
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 转换后的时间字符串
   */
  const convertFromBeijingToTeamTime = (
    dateInput: string | Date | dayjs.Dayjs,
    format = 'YYYY-MM-DD HH:mm:ss',
  ): string => {
    const teamTimezoneValue = teamTimezone.value

    // 将输入时间视为北京时区时间，转换为团队时区
    const beijingTime = dayjs.tz(dateInput, 'Asia/Shanghai')
    const teamTime = beijingTime.tz(teamTimezoneValue).locale('en')

    return teamTime.format(format)
  }

  /**
   * 将团队时区的时间转换为北京时区的时间
   * @param dateInput 团队时区的时间，可以是字符串、Date对象或dayjs对象
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 转换后的时间字符串
   */
  const convertFromTeamToBeijingTime = (
    dateInput: string | Date | dayjs.Dayjs,
    format = 'YYYY-MM-DD HH:mm:ss',
  ): string => {
    const teamTimezoneValue = teamTimezone.value

    // 将输入时间视为团队时区时间，转换为北京时区
    const teamTime = dayjs.tz(dateInput, teamTimezoneValue)
    const beijingTime = teamTime.tz('Asia/Shanghai').locale('en')

    return beijingTime.format(format)
  }

  /**
   * 获取团队时区的当前时间
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 团队时区的当前时间字符串
   */
  const getTeamCurrentTime = (format = 'YYYY-MM-DD HH:mm:ss'): string => {
    const teamTimezoneValue = teamTimezone.value
    return dayjs().tz(teamTimezoneValue).format(format)
  }

  /**
   * 获取北京时区的当前时间
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 北京时区的当前时间字符串
   */
  const getBeijingCurrentTime = (format = 'YYYY-MM-DD HH:mm:ss'): string => {
    return dayjs().tz('Asia/Shanghai').format(format)
  }

  /**
   * 根据团队时区创建dayjs对象
   * @param dateInput 时间输入
   * @returns 团队时区的dayjs对象
   */
  const createTeamDayjs = (dateInput?: string | Date | dayjs.Dayjs): dayjs.Dayjs => {
    const teamTimezoneValue = teamTimezone.value

    if (dateInput) {
      return dayjs.tz(dateInput, teamTimezoneValue)
    }

    return dayjs().tz(teamTimezoneValue)
  }

  /**
   * 根据北京时区创建dayjs对象
   * @param dateInput 时间输入
   * @returns 北京时区的dayjs对象
   */
  const createBeijingDayjs = (dateInput?: string | Date | dayjs.Dayjs): dayjs.Dayjs => {
    if (dateInput) {
      return dayjs.tz(dateInput, 'Asia/Shanghai')
    }

    return dayjs().tz('Asia/Shanghai')
  }

  return {
    // 计算属性
    currentTeam,
    teamCurrency,
    currencySymbol,
    teamTimezone,
    availableCurrencies,

    // 货币相关方法
    formatCurrency,
    getCurrencySymbol,
    isValidCurrency,

    // 时区转换方法
    convertFromBeijingToTeamTime,
    convertFromTeamToBeijingTime,
    getTeamCurrentTime,
    getBeijingCurrentTime,
    createTeamDayjs,
    createBeijingDayjs,

    // 常量
    CURRENCY_SYMBOLS,
  }
}
